import React from 'react';
import { Bot, Folder, PanelLeft, PlugZap, Radar, Radio, Search, Sparkles, SquarePen, Users } from 'lucide-react';

// The dashboard-icons CDN is missing marks for several of these (HubSpot,
// Snowflake, QuickBooks, Intercom), which rendered as broken images. Resolving
// through each brand's own domain is the pattern used elsewhere in the project
// and covers every tool we reference.
const TOOL_DOMAINS: Record<string, string> = {
  slack: 'slack.com',
  gmail: 'mail.google.com',
  notion: 'notion.so',
  salesforce: 'salesforce.com',
  linear: 'linear.app',
  hubspot: 'hubspot.com',
  snowflake: 'snowflake.com',
  stripe: 'stripe.com',
  quickbooks: 'quickbooks.intuit.com',
  figma: 'figma.com',
  intercom: 'intercom.com',
  ahrefs: 'ahrefs.com',
  github: 'github.com',
  sentry: 'sentry.io',
  mailchimp: 'mailchimp.com',
  granola: 'granola.ai',
  // AI surfaces the connections are exposed to.
  giga: 'getgiga.com',
  chatgpt: 'openai.com',
  claude: 'claude.ai',
  grok: 'x.ai'
};
export const toolLogo = (slug: string) => `https://www.google.com/s2/favicons?domain=${TOOL_DOMAINS[slug] ?? `${slug}.com`}&sz=64`;

/**
 * Shared chrome for every real-product screenshot on the page. One frame means
 * Connections, the platform primitives and the brain all read as the same
 * application rather than four unrelated illustrations.
 */
export function AppChrome({
  children,
  sidebar = true,
  active,
  className = ''
}: {
  children: React.ReactNode;
  sidebar?: boolean;
  active?: string;
  className?: string;
}) {
  // No browser bar: the fake URL and window chrome added a frame around the
  // frame without telling anyone anything they didn't already know.
  return <div className={`overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white shadow-[0_40px_100px_-40px_rgba(43,33,26,0.45)] ${className}`} data-id="element-189" data-mp="39kjlyn9154ri">
      <div className="flex min-h-[420px]" data-id="element-190" data-mp="1cr8and246el6">
        {sidebar && <AppSidebar active={active} data-id="element-191" data-mp="17sdkfq1thnr1" />}
        <div className="min-w-0 flex-1 bg-white" data-id="element-192" data-mp="17tg71ls3x415">{children}</div>
      </div>
    </div>;
}
const SIDEBAR_CHATS = ['Inbox catch-up', 'Renewal risk', 'Board deck notes'];
function AppSidebar({
  active
}: {
  active?: string;
}) {
  return <aside className="hidden w-[196px] shrink-0 flex-col border-r border-[#EFEAE1] bg-[#FCFAF6] px-2.5 py-3 lg:flex" data-id="element-193" data-mp="1hwtws9g38mq3">
      <div className="mb-3 flex items-center justify-between px-1.5" data-id="element-194" data-mp="1mvexe9z958f2">
        <span className="flex items-center gap-2" data-id="element-195" data-mp="1mtkawr8c188v">
          <span className="flex size-6 items-center justify-center rounded-lg bg-[#134E4A] text-[10px] font-bold text-white" data-id="element-196" data-mp="2amkdmq93u2pw">
            BW
          </span>
          <span className="text-[12.5px] font-semibold text-[#2B211A]" data-id="element-197" data-mp="20nbl81tw0w5n">
            Brightwave
          </span>
        </span>
        <PanelLeft size={13} className="text-neutral-300" data-id="element-198" data-mp="25lmx0i3qtgqi" />
      </div>
      <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#E7E0D4] bg-white px-2.5 py-1.5" data-id="element-199" data-mp="p86781ob3b6q">
        <SquarePen size={12} className="text-neutral-400" data-id="element-200" data-mp="u6zng1uz9gal" />
        <span className="text-[12px] font-semibold text-[#2B211A]" data-id="element-201" data-mp="u8kcjbyiw712">
          New chat
        </span>
      </div>
      <div className="mb-1 flex items-center gap-2 rounded-lg bg-white px-2 py-1.5 ring-1 ring-[#EFEAE1]" data-id="element-202" data-mp="mpkwnga9sxmg">
        <Search size={11} className="text-neutral-300" data-id="element-203" data-mp="vog38th889qa" />
        <span className="text-[11.5px] text-neutral-400" data-id="element-204" data-mp="vn4xio480rwj">Search</span>
      </div>
      <SidebarGroup label="Manage" items={MANAGE_ITEMS} active={active} data-id="element-205" data-mp="qqelqczg8igx" />
      <SidebarGroup label="Brain" items={BRAIN_ITEMS} active={active} data-id="element-206" data-mp="2zhhlmdwapg8" />
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-207" data-mp="2xo7ty7zjp40">
        Chats
      </p>
      <ul data-id="element-208" data-mp="cpam63fme4az">
        {SIDEBAR_CHATS.map(c => <li key={c} className="truncate rounded-lg px-2 py-[5px] text-[12px] font-medium text-neutral-500" data-id="element-209" data-mp="cqc66xem614u">
            {c}
          </li>)}
      </ul>
    </aside>;
}
const BRAIN_ITEMS = [{
  label: 'Tracks',
  icon: Folder
}, {
  label: 'Scans',
  icon: Radar
}, {
  label: 'Observations',
  icon: Sparkles
}];
const MANAGE_ITEMS = [{
  label: 'Assistants',
  icon: Bot
}, {
  label: 'Pulses',
  icon: Radio
}, {
  label: 'Connections',
  icon: PlugZap
}, {
  label: 'Groups',
  icon: Users
}];
function SidebarGroup({
  label,
  items,
  active
}: {
  label: string;
  items: Array<{
    label: string;
    icon: React.ElementType;
  }>;
  active?: string;
}) {
  return <>
      <p className="mt-3 px-2 pb-1 text-[9.5px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-210" data-mp="7m1k8yrlbysm">
        {label}
      </p>
      <ul data-id="element-211" data-mp="bmv7dpx7l9kg">
        {items.map(item => {
        const isActive = active === item.label;
        return <li key={item.label} className={`flex items-center gap-2 rounded-lg px-2 py-[5px] text-[12px] ${isActive ? 'bg-[#F1ECE3] font-semibold text-[#2B211A]' : 'font-medium text-neutral-500'}`} data-id="element-212" data-mp="gkwqubyjrawg">
              <item.icon size={11} strokeWidth={1.9} className={`shrink-0 ${isActive ? 'text-[#2B211A]' : 'text-neutral-300'}`} data-id="element-213" data-mp="qk5jn1hoejh8" />
              <span className="truncate" data-id="element-214" data-mp="2nfacwn4nngav">{item.label}</span>
            </li>;
      })}
      </ul>
    </>;
}